"use client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Image,
  Text,
  Box,
  Flex,
  IconButton,
  useDisclosure,
  Button,
  useBreakpointValue
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import { useGetImagePrompt } from "queries";
import { GET_USERNAME_BY_FILENAME } from "api";
import { fetch } from "libs";
import { GalleryImage } from "types";
import { CopyIcon } from "@chakra-ui/icons";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  image: GalleryImage;
  preloadedUsername?: string | null;
}

const ImageViewerModal = ({ isOpen, onClose, image, preloadedUsername }: Props) => {
  const finalFileName = image.file_name;
  const { data: promptData } = useGetImagePrompt(finalFileName, false);
// Inside your component
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        if (promptData?.prompt) {
          navigator.clipboard.writeText(promptData.prompt);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000); // hide after 2 sec
        }
      };
      const isMobile = useBreakpointValue({ base: true, md: false });

      const [username, setUsername] = useState<string | null>(preloadedUsername || null);

      useEffect(() => {
        if (preloadedUsername) return;
        if (!finalFileName) return;
      
        const fetchUsername = async () => {
          try {
            const res = await fetch(GET_USERNAME_BY_FILENAME(finalFileName));
            setUsername(res.username || null);
          } catch {
            setUsername(null);
          }
        };
      
        fetchUsername();
      }, [finalFileName, preloadedUsername]);
      

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered>
    <ModalOverlay />
    <ModalContent
      bg="#0A0A0A"
      borderRadius="lg"
      maxH="95vh"
      h="auto"
      p={6}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      maxW="6xl"
    >
      <ModalCloseButton color="white" />
      {isMobile ? (
  <Flex direction="column" gap={4} maxH="85vh" overflowY="auto">
    <Image
      src={image.file_name.startsWith("http") ? image.file_name : image.sas_url}
      alt="Generated Image"
      borderRadius="md"
      w="100%"
      h="auto"
      objectFit="contain"
      mb={4}
    />
    <Box px={1}>
      {username && (
        <Text fontWeight="bold" fontSize="md" color="#FF1B6D" mb={2}>
          Creator: {username}
        </Text>
      )}
      <Text fontWeight="semibold" color="gray.300" mb={2}>
        Prompt Used:
      </Text>
      <Box
        bg="rgba(255, 255, 255, 0.04)"
        p="4"
        borderRadius="md"
        color="white"
        fontSize="sm"
        border="1px solid rgba(255, 255, 255, 0.1)"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        maxH="160px"
        overflowY="auto"
        sx={{
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {promptData?.prompt || "No prompt found"}
      </Box>
      <Flex justify="flex-start" align="center" mt="4" gap="3">
        <Button
          size="sm"
          px="6"
          py="2"
          bgGradient="linear(to-r, #D91C5C, #702EFF)"
          color="white"
          _hover={{ opacity: 0.9 }}
          onClick={handleCopy}
          borderRadius="20px"
          leftIcon={<CopyIcon boxSize={4} />}
        >
          Copy Prompt
        </Button>
        {copied && (
          <Text fontSize="sm" color="green.300" fontWeight="semibold">
            Copied!
          </Text>
        )}
      </Flex>
    </Box>
  </Flex>
  ): ( <Flex
    direction={{ base: "column", md: "row" }}
    gap={8}
    align="flex-start"
    maxH="85vh"
    overflow="hidden"
  > 

<Flex justify="center" flex="1" maxH="85vh">
    <Image
      src={image.file_name.startsWith("http") ? image.file_name : image.sas_url}
      alt="Generated Image"
      borderRadius="md"
      maxH="85vh"
      w="auto"
      h="auto"
      maxW="100%"
      objectFit="contain"
    />
  </Flex>

  {/* Right: Prompt & Creator */}
  <Flex
    direction="column"
    flexShrink={0}
    w={{ base: "100%", md: "380px" }}
    overflowY="auto"
    maxH="85vh"
    pr={2}
    gap={4}
  >
    {/* Creator */}
    {username && (
      <Text fontWeight="bold" fontSize="lg" color="#FF1B6D">
        Creator: {username}
      </Text>
    )}

    {/* Prompt */}
    <Box>
      <Text fontWeight="semibold" color="gray.300" mb="2">
        Prompt Used:
      </Text>
      <Box
        bg="rgba(255, 255, 255, 0.04)"
        p="4"
        borderRadius="md"
        color="white"
        fontSize="sm"
        border="1px solid rgba(255, 255, 255, 0.1)"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        maxH="200px"
        overflowY="auto"
        sx={{
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {promptData?.prompt || "No prompt found"}
      </Box>
      <Flex justify="flex-start" align="center" mt="4" gap="3">
        <Button
          size="sm"
          px="6"
          py="2"
          bgGradient="linear(to-r, #D91C5C, #702EFF)"
          color="white"
          _hover={{ opacity: 0.9 }}
          onClick={handleCopy}
          borderRadius="20px"
          leftIcon={<CopyIcon boxSize={4} />}
        >
          Copy Prompt
        </Button>
        {copied && (
    <Text fontSize="sm" color="green.300" fontWeight="semibold">
      Copied!
    </Text>
  )}
      </Flex>
    </Box>
  </Flex>
 {/* existing desktop layout here */}
 </Flex>
)}
    </ModalContent>
  </Modal>
  );
};

export default ImageViewerModal;
